"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { Expand, X } from "lucide-react";

/**
 * Captioned product screenshot for marketing surfaces.
 *
 * The audit (joinmaren-page-audit.md, fix #1) flagged that a single
 * stylized chat illustration was the entire product demo. Real screens
 * are the highest-leverage credibility lift available to a pre-launch
 * landing page. This component renders one screen in a <figure> with
 * a single caption, framed in a sage-border card so it reads as
 * intentional (not a leaked dev screenshot).
 *
 * Drop the underlying PNGs into frontend/public/marketing/. Match the
 * filename used in the call site. The component uses next/image so the
 * image is served as AVIF/WebP where supported, with lazy loading by
 * default. Pass `priority` for the first screenshot above the fold so
 * the LCP image isn't lazy-deferred.
 *
 * `expandable` adds a tap-to-enlarge overlay: full-viewport charcoal
 * scrim, image at natural resolution (horizontally scrollable on small
 * screens), closed by Escape, the backdrop, or the close button. Dense
 * app screens are unreadable at phone widths without it.
 */
type ScreenshotProps = {
  src: string;
  alt: string;
  caption?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  expandable?: boolean;
};

export default function Screenshot({
  src,
  alt,
  caption,
  width = 1440,
  height = 900,
  priority = false,
  expandable = false,
}: ScreenshotProps) {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    // Lock background scroll while the overlay is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  const framed = (
    <div className="relative overflow-hidden rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal shadow-lg">
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        priority={priority}
        quality={90}
        // sizes mirrors the container widths after the round-3 layout:
        // pillar screenshots run full section width (max-w-5xl ~1024px),
        // the hero shot about half the 7xl container; phones full-bleed.
        sizes="(min-width: 1280px) 1024px, (min-width: 640px) 90vw, 100vw"
        className="w-full h-auto block"
      />
      {expandable && (
        <span
          aria-hidden
          className="absolute bottom-2.5 right-2.5 flex h-8 w-8 items-center justify-center rounded-lg bg-maren-charcoal/60 text-maren-stone backdrop-blur-sm transition group-hover:bg-maren-charcoal/80"
        >
          <Expand className="h-4 w-4" />
        </span>
      )}
    </div>
  );

  return (
    <figure className="w-full">
      {expandable ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Enlarge screenshot: ${alt}`}
          className="group block w-full cursor-zoom-in text-left focus:outline-none focus:ring-2 focus:ring-maren-gold/50 rounded-2xl"
        >
          {framed}
        </button>
      ) : (
        framed
      )}
      {caption ? (
        <figcaption className="mt-4 text-center text-sm text-maren-charcoal/65 dark:text-maren-stone/70 leading-relaxed">
          {caption}
        </figcaption>
      ) : null}

      {expandable && open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          className="fixed inset-0 z-50 flex items-center justify-center bg-maren-charcoal/90 p-4 sm:p-8"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close enlarged screenshot"
            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-maren-stone/10 text-maren-stone hover:bg-maren-stone/20 transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
          {/* Natural-resolution image; overflow scrolls on small screens so
              phone users can pan instead of squint. stopPropagation keeps a
              pan/tap on the image from closing the overlay. */}
          <div
            className="max-h-full max-w-full overflow-auto rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={src} alt={alt} className="block max-w-none sm:max-w-full h-auto" />
          </div>
        </div>
      )}
    </figure>
  );
}
